'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { LuLayoutDashboard, LuShieldCheck, LuCreditCard, LuX } from 'react-icons/lu';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { useAuthControls } from '@core/ui/auth/useAuthControls';
import { Badge } from '@core/ui/primitives/badge';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@core/ui/primitives/dialog';
import {
  Sidebar,
  SidebarGroup,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from '@core/ui/primitives/sidebar';
import { Spinner } from '@core/ui/primitives/spinner';
import {
  loadSecurityAdminData,
  type SecurityAdminData,
} from '@/app/admin/security/actions';
import {
  loadSubscriptionAdminData,
  type SubscriptionAdminData,
} from '@/app/admin/subscription/actions';
import { SecurityAdminView } from '@/app/admin/security/components/SecurityAdminView';
import { SubscriptionAdminView } from '@/app/admin/subscription/components/SubscriptionAdminView';

/**
 * PlatformConsoleDialog - Super-admin modal for platform administration
 *
 * Structure:
 * ┌─────────────────────────────────────────────────────────────────────┐
 * │ Left Sidebar     │ Main Content                                     │
 * │ - Overview       │                                                  │
 * │ - Security       │  (tab content)                                   │
 * │ - Subscriptions  │                                                  │
 * └──────────────────┴──────────────────────────────────────────────────┘
 *
 * Uses the same styling pattern as OrganizationDialog:
 * - Sidebar primitive with token colors (bg-sidebar, text-sidebar-foreground)
 * - SidebarMenu/SidebarMenuItem/SidebarMenuButton for navigation
 * - SidebarInset for main content
 */

type NavSection = 'overview' | 'security' | 'subscriptions';

interface NavItem {
  key: NavSection;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const NAV_ITEMS: NavItem[] = [
  { key: 'overview', label: 'Overview', icon: LuLayoutDashboard },
  { key: 'security', label: 'Security', icon: LuShieldCheck },
  { key: 'subscriptions', label: 'Subscriptions', icon: LuCreditCard },
];

interface PlatformConsoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PlatformConsoleDialog({ open, onOpenChange }: PlatformConsoleDialogProps) {
  const { user } = useAuthControls();
  const [activeSection, setActiveSection] = useState<NavSection>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [securityData, setSecurityData] = useState<SecurityAdminData | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionAdminData | null>(null);

  // Load data when dialog opens
  const loadData = useCallback(async () => {
    if (!open || !user?.isSuperAdmin) return;

    setLoading(true);
    setError(null);

    try {
      const [security, subscription] = await Promise.all([
        loadSecurityAdminData(),
        loadSubscriptionAdminData(),
      ]);
      setSecurityData(security);
      setSubscriptionData(subscription);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load admin data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [open, user?.isSuperAdmin]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, loadData]);

  // Reset to overview when dialog closes
  useEffect(() => {
    if (!open) {
      setActiveSection('overview');
    }
  }, [open]);

  // Don't render for non-super-admins
  if (!user?.isSuperAdmin) {
    return null;
  }

  const activeNavItem = NAV_ITEMS.find((i) => i.key === activeSection);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className={cn(
          'w-full sm:max-w-5xl h-[50rem] min-h-[50rem] max-h-[50rem] overflow-hidden gap-0 p-0 shadow-2xl sm:rounded-3xl'
        )}>
        <DialogTitle className='sr-only'>Platform Console</DialogTitle>
        <DialogDescription className='sr-only'>
          Manage platform security and subscriptions.
        </DialogDescription>
        <SidebarProvider className='min-w-0'>
          <div className='flex h-full min-h-0 w-full min-w-0 bg-background max-h-[50rem]'>
            {/* Sidebar - using Sidebar primitive like OrganizationDialog */}
            <Sidebar collapsible='none' className='bg-sidebar dark:bg-sidebar-dark/80'>
              <div className='flex h-full flex-1 flex-col'>
                {/* Header */}
                <div className='flex h-14 items-center gap-2 px-4'>
                  <div className='flex flex-col'>
                    <span className='text-sm font-semibold text-sidebar-foreground'>
                      Platform Console
                    </span>
                    <span className='text-xs text-muted-foreground'>Super Admin</span>
                  </div>
                </div>

                {/* Navigation */}
                <SidebarGroup className='flex-1 px-2 py-2'>
                  <SidebarMenu>
                    {NAV_ITEMS.map((item) => {
                      const isActive = activeSection === item.key;
                      return (
                        <SidebarMenuItem key={item.key}>
                          <SidebarMenuButton
                            type='button'
                            tooltip={item.label}
                            isActive={isActive}
                            onClick={() => setActiveSection(item.key)}
                            className='group/sidebar-link overflow-hidden justify-start text-left transition-all duration-300 ease-sidebar-smooth group-data-[collapsed=true]/sidebar-wrapper:justify-center group-data-[collapsed=true]/sidebar-wrapper:px-0 data-[active=true]:bg-muted-foreground/15 data-[active=true]:text-foreground data-[active=true]:font-semibold focus-visible:ring-0'>
                            <item.icon className='h-4 w-4 transition-transform duration-300 ease-sidebar-smooth group-data-[collapsed=true]/sidebar-wrapper:scale-90' />
                            <span className='flex-1 truncate text-sm font-medium transition-[opacity,transform] duration-200 ease-sidebar-smooth group-data-[collapsed=true]/sidebar-wrapper:opacity-0 group-data-[collapsed=true]/sidebar-wrapper:translate-x-3 group-data-[collapsed=true]/sidebar-wrapper:pointer-events-none'>
                              {item.label}
                            </span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroup>

                {/* Footer */}
                <div className='px-4 py-4'>
                  <div className='text-xs text-muted-foreground'>
                    <p className='truncate font-medium text-sidebar-foreground'>{user.email}</p>
                    <Badge variant='outline' className='mt-2 text-[10px]'>
                      Super Admin
                    </Badge>
                  </div>
                </div>
              </div>
            </Sidebar>

            {/* Main Content - using SidebarInset like OrganizationDialog */}
            <SidebarInset className='flex min-h-0 min-w-0 flex-1 flex-col'>
              {/* Header - matching OrganizationDialog styling */}
              <header className='flex py-7 h-8 items-center gap-2 border-b border-gray-200/60 px-4 dark:border-gray-800/80'>
                <div className='flex flex-col'>
                  <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                    Platform Console
                  </span>
                  <span className='text-sm font-medium text-gray-900 dark:text-gray-100'>
                    {activeNavItem?.label ?? 'Overview'}
                  </span>
                </div>
                <div className='flex flex-1 items-center justify-end gap-1'>
                  <DialogClose asChild>
                    <button
                      type='button'
                      className='inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      aria-label='Close'>
                      <LuX className='h-3.5 w-3.5' />
                    </button>
                  </DialogClose>
                </div>
              </header>

              {/* Content Area */}
              <div className='flex flex-1 min-h-0 min-w-0 flex-col overflow-y-auto overflow-x-auto p-4 pt-3 scrollbar-fade'>
                {error && <div className='text-sm text-rose-600'>{error}</div>}
                {loading ? (
                  <div className='flex h-full items-center justify-center'>
                    <Badge variant='outline' className='gap-2'>
                      <Spinner />
                      Loading...
                    </Badge>
                  </div>
                ) : (
                  <>
                    {activeSection === 'overview' && (
                      <OverviewSection
                        securityData={securityData}
                        subscriptionData={subscriptionData}
                        onNavigate={setActiveSection}
                      />
                    )}
                    {activeSection === 'security' && securityData && (
                      <div className='flex-1 min-w-0 overflow-auto scrollbar-fade'>
                        <SecurityAdminView {...securityData} onRefresh={loadData} />
                      </div>
                    )}
                    {activeSection === 'subscriptions' && subscriptionData && (
                      <div className='flex-1 min-w-0 overflow-auto scrollbar-fade'>
                        <SubscriptionAdminView {...subscriptionData} onRefresh={loadData} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </SidebarInset>
          </div>
        </SidebarProvider>
      </DialogContent>
    </Dialog>
  );
}

interface OverviewSectionProps {
  securityData: SecurityAdminData | null;
  subscriptionData: SubscriptionAdminData | null;
  onNavigate: (section: NavSection) => void;
}

function OverviewSection({ securityData, subscriptionData, onNavigate }: OverviewSectionProps) {
  const stats = useMemo(() => {
    const profileCount = securityData?.profiles.length ?? 0;
    const roleCount = securityData?.roles.length ?? 0;
    const tierCount = subscriptionData?.tiers.length ?? 0;
    const orgCount = subscriptionData?.organizations.length ?? 0;

    return { profileCount, roleCount, tierCount, orgCount };
  }, [securityData, subscriptionData]);

  return (
    <div className='space-y-6'>
      <div>
        <h3 className='text-lg font-semibold text-foreground mb-2'>Platform Console</h3>
        <p className='text-sm text-muted-foreground'>
          Central hub for platform-wide administration. Manage security policies, subscription
          tiers, and operational settings.
        </p>
      </div>

      <div className='grid gap-4 md:grid-cols-2'>
        {/* Security Card */}
        <button
          type='button'
          onClick={() => onNavigate('security')}
          className='group text-left rounded-xl border border-border bg-card p-6 transition-all hover:border-primary hover:shadow-md'>
          <div className='flex items-center gap-3 mb-3'>
            <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10'>
              <LuShieldCheck className='h-5 w-5 text-primary' />
            </div>
            <div>
              <h4 className='font-semibold text-foreground'>Security</h4>
              <p className='text-xs text-muted-foreground'>Roles, profiles & privileges</p>
            </div>
          </div>
          <div className='flex gap-4 text-sm'>
            <div>
              <span className='font-semibold text-foreground'>{stats.profileCount}</span>
              <span className='text-muted-foreground ml-1'>Profiles</span>
            </div>
            <div>
              <span className='font-semibold text-foreground'>{stats.roleCount}</span>
              <span className='text-muted-foreground ml-1'>Roles</span>
            </div>
          </div>
        </button>

        {/* Subscriptions Card */}
        <button
          type='button'
          onClick={() => onNavigate('subscriptions')}
          className='group text-left rounded-xl border border-border bg-card p-6 transition-all hover:border-primary hover:shadow-md'>
          <div className='flex items-center gap-3 mb-3'>
            <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10'>
              <LuCreditCard className='h-5 w-5 text-primary' />
            </div>
            <div>
              <h4 className='font-semibold text-foreground'>Subscriptions</h4>
              <p className='text-xs text-muted-foreground'>Tiers, quotas & billing</p>
            </div>
          </div>
          <div className='flex gap-4 text-sm'>
            <div>
              <span className='font-semibold text-foreground'>{stats.tierCount}</span>
              <span className='text-muted-foreground ml-1'>Tiers</span>
            </div>
            <div>
              <span className='font-semibold text-foreground'>{stats.orgCount}</span>
              <span className='text-muted-foreground ml-1'>Organizations</span>
            </div>
          </div>
        </button>
      </div>

      <div className='rounded-lg border border-dashed border-border p-4'>
        <p className='text-xs text-muted-foreground'>
          <strong className='text-foreground'>Note:</strong> Platform Console access is restricted to super administrators.
          All actions are logged for audit purposes.
        </p>
      </div>
    </div>
  );
}

export default PlatformConsoleDialog;
